import { useEffect } from 'react'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { useUnsavedChangesStore } from '@/stores/unsavedChangesStore'

/**
 * Global unsaved-changes confirmation, mounted once in DashboardLayout. Any
 * navigation trigger that calls unsavedChangesStore.requestNavigation()
 * surfaces here -- see docs/superpowers/specs/2026-08-06-settings-unsaved-changes-guard-design.md.
 * Also covers browser tab close/refresh via the native beforeunload prompt.
 */
export function UnsavedChangesPrompt() {
  const isDirty = useUnsavedChangesStore((s) => s.isDirty)
  const pendingAction = useUnsavedChangesStore((s) => s.pendingAction)
  const confirmNavigation = useUnsavedChangesStore((s) => s.confirmNavigation)
  const cancelNavigation = useUnsavedChangesStore((s) => s.cancelNavigation)

  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (!isDirty) return
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [isDirty])

  return (
    <ConfirmDialog
      open={pendingAction !== null}
      title="Leave without saving?"
      description="You have unsaved changes on this page. If you leave now, they'll be lost."
      confirmLabel="Leave"
      cancelLabel="Stay"
      tone="destructive"
      onConfirm={confirmNavigation}
      onCancel={cancelNavigation}
    />
  )
}

export default UnsavedChangesPrompt
